import { toast } from 'react-toastify';
import { api } from '~/components/Providers/APIClient';
import {
    createPackTierFormInput,
    createPackTierFormSchema,
    packFormInput,
    packFormSchema,
    packTierFormInput,
} from '~/util/forms/packtier';
import { ApiMutationOptions } from '~/util/hooks/useApiMutation';
import { BasePack, PackTier } from '~/util/types/packs';

export const createPacktierMutation = (): ApiMutationOptions<
    PackTier,
    createPackTierFormInput
> => ({
    customMutationFn: async (values) => {
        const {
            tier: { list, ...packTier },
            pack,
        } = createPackTierFormSchema.parse(values);

        const tierResponse = await api.send<PackTier>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/pack-tiers`,
            method: 'POST',
            attachAuth: true,
            body: packTier,
        });

        if (tierResponse.error) {
            throw tierResponse;
        }

        const packResponse = await api.send<BasePack>({
            path: `/${list === 'platformer' ? 'arepl' : 'aredl'}/packs`,
            method: 'POST',
            body: {
                name: pack.name,
                tier: tierResponse.data.id,
            },
            attachAuth: true,
        });

        if (packResponse.error) {
            toast.error(
                `Failed to add the new pack: ${packResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
            return tierResponse;
        }

        const levelsResponse = await api.send<string[]>({
            path: `/${list === 'platformer' ? 'arepl' : 'aredl'}/packs/${packResponse.data.id}/levels`,
            method: 'PATCH',
            body: pack.levels,
            attachAuth: true,
        });

        if (levelsResponse.error) {
            toast.error(
                `Failed to add levels to the new pack: ${levelsResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
        }

        return tierResponse;
    },
    invalidateQueries: ({
        variables: {
            tier: { list },
        },
    }) => ['packs', list],
    toastMessages: {
        loading: 'Creating pack tier...',
        error: 'Failed to create pack tier: ',
        success: 'Pack tier created successfully',
    },
});

export type updatePacktierMutationType = {
    data: packTierFormInput;
    id: string;
};
export const updatePacktierMutation = (): ApiMutationOptions<
    PackTier,
    updatePacktierMutationType
> => ({
    buildRequest: ({ data: { list, ...packTier }, id }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/pack-tiers/${id}`,
            method: 'PATCH',
            attachAuth: true,
            body: packTier,
        };
    },
    invalidateQueries: ({
        variables: {
            data: { list },
        },
    }) => ['packs', list],
    toastMessages: {
        loading: 'Updating pack tier...',
        error: 'Failed to update pack tier: ',
        success: 'Pack tier updated successfully',
    },
});

export type deletePacktierMutationType = {
    list: 'classic' | 'platformer';
    id: string;
};
export const deletePacktierMutation = (): ApiMutationOptions<
    void,
    deletePacktierMutationType
> => ({
    buildRequest: ({ list, id }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/pack-tiers/${id}`,
            method: 'DELETE',
            attachAuth: true,
        };
    },
    invalidateQueries: ({ variables: { list } }) => ['packs', list],
    toastMessages: {
        loading: 'Deleting pack tier...',
        error: 'Failed to delete pack tier: ',
        success: 'Pack tier deleted successfully',
    },
});

export const createPackMutation = (): ApiMutationOptions<
    BasePack,
    packFormInput
> => ({
    customMutationFn: async (values) => {
        const { list, pack, levels } = packFormSchema.parse(values);

        const packResponse = await api.send<BasePack>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/packs`,
            method: 'POST',
            attachAuth: true,
            body: pack,
        });

        if (packResponse.error) {
            throw packResponse;
        }

        const levelsResponse = await api.send<string[]>({
            path: `/${list === 'platformer' ? 'arepl' : 'aredl'}/packs/${packResponse.data.id}/levels`,
            method: 'PATCH',
            body: levels,
            attachAuth: true,
        });

        if (levelsResponse.error) {
            toast.error(
                `Failed to add levels to the new pack: ${levelsResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
        }

        return packResponse;
    },
    invalidateQueries: ({ variables: { list } }) => ['packs', list],
    toastMessages: {
        loading: 'Creating pack...',
        error: 'Failed to create pack: ',
        success: 'Pack created successfully',
    },
});

export type updatePackMutationType = {
    data: packFormInput;
    id: string;
};

export const updatePackMutation = (): ApiMutationOptions<
    BasePack,
    updatePackMutationType
> => ({
    customMutationFn: async ({ data, id }) => {
        const { list, pack, levels } = packFormSchema.parse(data);

        const packResponse = await api.send<BasePack>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/packs/${id}`,
            method: 'PATCH',
            attachAuth: true,
            body: pack,
        });

        if (packResponse.error) {
            throw packResponse;
        }

        const levelsResponse = await api.send<string[]>({
            path: `/${list === 'platformer' ? 'arepl' : 'aredl'}/packs/${packResponse.data.id}/levels`,
            method: 'POST',
            body: levels,
            attachAuth: true,
        });

        if (levelsResponse.error) {
            toast.error(
                `Failed to update levels: ${levelsResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
        }

        return packResponse;
    },
    invalidateQueries: ({
        variables: {
            data: { list },
        },
    }) => ['packs', list],
    toastMessages: {
        loading: 'Updating pack...',
        error: 'Failed to update pack: ',
        success: 'Pack updated successfully',
    },
});

export type deletePackMutationType = {
    list: 'classic' | 'platformer';
    id: string;
};
export const deletePackMutation = (): ApiMutationOptions<
    void,
    deletePackMutationType
> => ({
    buildRequest: ({ list, id }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/packs/${id}`,
            method: 'DELETE',
            attachAuth: true,
        };
    },
    invalidateQueries: ({ variables: { list } }) => ['packs', list],
    toastMessages: {
        loading: 'Deleting pack...',
        error: 'Failed to delete pack: ',
        success: 'Pack deleted successfully',
    },
});
